import React, { useState, useContext, useEffect } from 'react';
import { UserContext } from '../context/UserContext';
import useApps from '../hooks/useApps';
import API from '../config/axiosConfig';
import { Button, DialogActions } from '@mui/material';
import { Skeleton } from '@mui/material';
import { dialogActionsStyle, primaryButtonStyle, secondaryButtonStyle } from '../utils/sharedStyles';
import LoadingSubmitButton from '../components/LoadingSubmitButton'; 
import { cancelButton, confirmButton } from '../components/Buttons';


interface Department {
  _id: string;
  nameHebrew: string;
  nameEnglish: string;
  hospitalName: string;
  hospitalId: string;
  apps: any[];
}

interface AppsChooserProps {
  department: Department;
  onClose: () => void;
}

interface App {
  _id: string;
  key: string;
  title: string;
  icon: string;
  description: string;
  status: string;
}

const AppsChooser: React.FC<AppsChooserProps> = ({ department, onClose }) => {
  const { apps, loading } = useApps();
  const { fetchHospitals } = useContext(UserContext);
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Initialize selected apps based on department's current apps
    if (department.apps) {
      const currentAppIds = department.apps.map(app => 
        typeof app === 'string' ? app : app._id
      );
      setSelectedAppIds(currentAppIds);
    }
  }, [department.apps]);

  const handleAppToggle = (appId: string) => {
    setSelectedAppIds(prev => {
      const appsArray = apps as App[];
      const currentApp = appsArray.find(app => app._id === appId);
      
      if (prev.includes(appId)) {
        // App is being unchecked
        return prev.filter(id => id !== appId);
      } else {
        // App is being checked
        let newSelectedIds = [...prev, appId];
        
        // If the selected app is RESUS, also select RESUS_NURSING
        if (currentApp?.key === 'RESUS') {
          const resusNursingApp = appsArray.find(app => app.key === 'RESUS_NURSING');
          if (resusNursingApp && !newSelectedIds.includes(resusNursingApp._id)) {
            newSelectedIds.push(resusNursingApp._id);
          }
        }
        
        return newSelectedIds;
      }
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await API.put(
        `/api/hospitals/${department.hospitalId}/departments/${department._id}/apps`,
        { appIds: selectedAppIds }
      );
      
      // Refresh hospitals data to get updated department apps
      await fetchHospitals();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const renderAppItem = (app: App) => (
    <div
      key={app._id}
      style={{
        ...appItemStyle,
        backgroundColor: selectedAppIds.includes(app._id) ? '#f5f5f5' : 'white',
        boxShadow: '0 0 5px -1px rgba(0, 0, 0, 0.2)'
      }}
      onClick={() => handleAppToggle(app._id)}
    >
      <input
        type="checkbox"
        checked={selectedAppIds.includes(app._id)}
        onChange={() => {}}
        style={checkboxStyle}
        readOnly
      />
      <div style={appInfoStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={appTitleStyle}>
            <span>{app.title}</span>
            {app.status === 'inDevelopment' && (
              <span style={developmentBadgeStyle}>בפיתוח</span>
            )}
          </div>
        </div>
        <div style={appKeyStyle}>{app.description || ''}</div>
      </div>
    </div>
  );

  const sortedApps = [...(apps as App[])].sort((a, b) => a.title.localeCompare(b.title));
  const readyApps = sortedApps.filter(app => app.status !== 'inDevelopment');
  const inDevelopmentApps = sortedApps.filter(app => app.status === 'inDevelopment');

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <h3>בחר אפליקציות עבור {department.nameHebrew}</h3>
          <button onClick={onClose} style={closeButtonStyle}>×</button>
        </div>

        <div style={contentStyle}>
          {loading ? (
            <SkeletonChooser />
          ) : (
            <div style={appsGridStyle}>
              {readyApps.map(renderAppItem)}
              {inDevelopmentApps.length > 0 && (
                <div style={developmentNoticeStyle}>
                  האפליקציות שלהלן הן אפליקציות שעדיין בפיתוח, ולא אושרו לשימוש או תוקפו בידי איש
                  צוות רפואי. הן כאן להתרשמות בלבד. ניתן להוסיף אותן, אך אין להתסמך עליהן בשום עניין.
                  לעוד פרטים, ניתן ליצור איתנו קשר ב{' '}
                  <a href="mailto:mashlom.me@gmail.com" style={developmentNoticeLinkStyle}>
                    mashlom.me@gmail.com
                  </a>
                </div>
              )}
              {inDevelopmentApps.map(renderAppItem)}
            </div>
          )}
        </div>
        <DialogActions style={dialogActionsStyle}>
          <Button style={cancelButton as React.CSSProperties} onClick={onClose} >ביטול</Button>
           <LoadingSubmitButton
              onClick={handleSave}
              isLoading={saving}
              text="שמור"
              loadingText="שומר..."
              style={confirmButton as React.CSSProperties} 
            />
        </DialogActions>
      </div>
    </div>
  );
};

const SkeletonChooser = () => {
  return (
    <div style={appsGridStyle}>
      {Array.from(new Array(6)).map((_, index) => (
        <div key={index} style={{...appItemStyle, boxShadow: '0 0 5px -1px rgba(0, 0, 0, 0.2)'}}>
          <Skeleton variant="rectangular" width={20} height={20} style={{marginLeft: '12px'}} />
          <div style={{flex: 1}}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="80%" />
          </div>
        </div>
      ))}
    </div>
  )
}

// Styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  width: '90%',
  maxWidth: '600px',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px',
  borderBottom: '1px solid #eee',
  textAlign: 'right',
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: '24px',
  cursor: 'pointer',
  color: '#666',
};

const contentStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '20px',
};

const appsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: '12px',
};

const appItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '12px',
  borderRadius: '6px',
  cursor: 'pointer',
  border: '1px solid #ddd',
  transition: 'background-color 0.2s',
};

const checkboxStyle: React.CSSProperties = {
  marginLeft: '12px',
  cursor: 'pointer',
};

const appInfoStyle: React.CSSProperties = {
  flex: 1,
  textAlign: 'right',
};

const appTitleStyle: React.CSSProperties = {
  fontWeight: 'bold',
  marginBottom: '4px',
};

const appKeyStyle: React.CSSProperties = {
  fontSize: '0.9em',
  color: '#666',
};

const developmentBadgeStyle: React.CSSProperties = {
  color: 'white',
  backgroundColor: '#F77389',
  padding: '2px 8px',
  marginRight: '8px',
  borderRadius: '16px',
  fontSize: '0.75em',
  fontWeight: 'normal',
};

const developmentNoticeStyle: React.CSSProperties = {
  gridColumn: '1 / -1',
  marginTop: '8px',
  padding: '12px',
  borderRadius: '6px',
  border: '1px solid #F77389',
  backgroundColor: '#FDF1F3',
  color: '#8a3b49',
  fontSize: '0.85em',
  lineHeight: 1.6,
  textAlign: 'right',
};

const developmentNoticeLinkStyle: React.CSSProperties = {
  color: '#8a3b49',
  fontWeight: 'bold',
  direction: 'ltr',
  display: 'inline-block',
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  padding: '20px',
  borderTop: '1px solid #eee',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  border: '1px solid #ddd',
  backgroundColor: 'white',
  borderRadius: '4px',
  cursor: 'pointer',
};

const saveButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  border: 'none',
  backgroundColor: '#007bff',
  color: 'white',
  borderRadius: '4px',
  cursor: 'pointer',
};

export default AppsChooser;